import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import '../styles/MyOrders.css';

const MyOrders = () => {
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [orderToCancel, setOrderToCancel] = useState(null);

  // Fetch user-specific orders
  useEffect(() => {
    const fetchOrders = async () => {
      setIsLoading(true);
      setError('');
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/orders`, {
          method: 'GET',
          credentials: 'include',
        });
        if (response.ok) {
          const data = await response.json();
          setOrders(data);
        } else {
          setError('Failed to fetch orders');
          setOrders([]);
        }
      } catch (error) {
        setError('Network error. Please check your connection.');
        setOrders([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrders();

    // Intersection Observer for animations
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('mo-visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.mo-animate');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  // Initiate order cancellation
  const handleOrderCancel = (id) => {
    setOrderToCancel(id);
    setShowCancelModal(true);
  };

  // Confirm order cancellation
  const confirmCancel = async () => {
    if (!orderToCancel) return;
    setIsLoading(true);
    setError('');
    setSuccess('');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/orders/${orderToCancel}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        toast.success('Order canceled successfully');
        setSuccess('Order canceled successfully');
        const refreshResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/orders`, {
          credentials: 'include',
        });
        if (refreshResponse.ok) {
          setOrders(await refreshResponse.json());
        } else {
          setError('Failed to refresh orders');
        }
      } else {
        const errorData = await response.json();
        const message = errorData.message || 'Failed to cancel order';
        toast.error(message);
        setError(message);
      }
    } catch (error) {
      const message = 'Network error. Please try again.';
      toast.error(message);
      setError(message);
    } finally {
      setIsLoading(false);
      setShowCancelModal(false);
      setOrderToCancel(null);
    }
  };

  // Close cancellation modal
  const closeCancelModal = () => {
    setShowCancelModal(false);
    setOrderToCancel(null);
  };

  // Handle order card click to show details
  const handleOrderClick = (order) => {
    setSelectedOrder(order);
  };

  // Close order details modal
  const closeModal = () => {
    setSelectedOrder(null);
  };

  // Check if cancellation is allowed
  const canCancelOrder = (order) => {
    if (order.status !== 'active') return false;
    const startDate = new Date(order.dateFrom);
    if (isNaN(startDate)) {
      console.error('Invalid start date:', order.dateFrom);
      return false;
    }
    const now = new Date();
    const oneDayInMs = 24 * 60 * 60 * 1000;
    return startDate - now >= oneDayInMs;
  };

  // Format date for display
  const formatDateTime = (isoDate) => {
    if (!isoDate) return 'Invalid Date';
    const dateObj = new Date(isoDate);
    if (isNaN(dateObj)) return 'Invalid Date';
    return dateObj.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };

  return (
    <div className="mo-container">
      {/* Banner Section */}
      <section className="mo-banner mo-animate">
        <div className="mo-banner-overlay">
          <div className="mo-banner-content">
            <h1 className="mo-banner-title">My Orders</h1>
            <p className="mo-banner-subtitle">Manage your vehicle rentals with ease</p>
          </div>
        </div>
      </section>

      {/* Orders Section */}
      <section className="mo-orders-section">
        <div className="mo-wrapper">
          <header className="mo-section-header mo-animate">
            <h2>Your Orders</h2>
            <div className="mo-divider"></div>
          </header>

          {error && <div className="mo-message mo-message-error">{error}</div>}
          {success && <div className="mo-message mo-message-success">{success}</div>}

          <div className="mo-orders-grid mo-animate">
            {orders.length > 0 ? (
              orders.map((order) => (
                <article
                  key={order._id}
                  className="mo-order-card"
                  onClick={() => handleOrderClick(order)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && handleOrderClick(order)}
                >
                  <header className="mo-card-header">
                    <h3>{order.vehicleId?.name || 'Unknown'}</h3>
                    <span className={`mo-status mo-status-${order.status}`}>
                      {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                    </span>
                  </header>
                  <div className="mo-card-body">
                    <p>
                      <strong>Order ID:</strong> {order._id.slice(-6)}
                    </p>
                    <p>
                      <strong>Rental Period:</strong>{' '}
                      {`${formatDateTime(order.dateFrom)} - ${formatDateTime(order.dateTo)}`}
                    </p>
                    <p>
                      <strong>Total Price:</strong> ₹{order.totalPrice}
                    </p>
                  </div>
                  {canCancelOrder(order) && (
                    <footer className="mo-card-footer">
                      <button
                        className="mo-button mo-button-danger mo-button-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOrderCancel(order._id);
                        }}
                        disabled={isLoading}
                        aria-label={`Cancel order ${order._id.slice(-6)}`}
                      >
                        Cancel
                      </button>
                    </footer>
                  )}
                </article>
              ))
            ) : (
              <p className="mo-no-orders">No orders found. Book a vehicle now!</p>
            )}
          </div>
        </div>
      </section>

      {/* Order Details Modal */}
      {selectedOrder && (
        <div
          className="mo-modal-overlay"
          role="dialog"
          aria-labelledby="modal-title"
          onClick={closeModal}
        >
          <div
            className="mo-modal"
            onClick={(e) => e.stopPropagation()}
            role="document"
            tabIndex={-1}
          >
            <header className="mo-modal-header">
              <h2 id="modal-title">Order Details</h2>
              <button
                className="mo-modal-close"
                onClick={closeModal}
                aria-label="Close modal"
              >
                ×
              </button>
            </header>
            <div className="mo-modal-body">
              <p>
                <strong>Order ID:</strong> {selectedOrder._id}
              </p>
              <p>
                <strong>Vehicle:</strong> {selectedOrder.vehicleId?.name || 'Unknown'}
              </p>
              <p>
                <strong>Name:</strong> {selectedOrder.name} | <strong>Email:</strong>{' '}
                {selectedOrder.email || 'N/A'}
              </p>
              <p>
                <strong>Contact:</strong> {selectedOrder.contact}
              </p>
              <p>
                <strong>Address:</strong> {selectedOrder.address}
              </p>
              <p>
                <strong>Rental Period:</strong>{' '}
                {`${formatDateTime(selectedOrder.dateFrom)} to ${formatDateTime(
                  selectedOrder.dateTo
                )}`}
              </p>
              <p>
                <strong>Pricing Type:</strong> {selectedOrder.pricingType}
              </p>
              <p>
                <strong>Total Price (with 5% GST):</strong> ₹{selectedOrder.totalPrice}
              </p>
              <p>
                <strong>Agreed to Terms:</strong> {selectedOrder.agreeTerms ? 'Yes' : 'No'}
              </p>
              <p>
                <strong>Has License:</strong> {selectedOrder.hasLicense ? 'Yes' : 'No'}
              </p>
              <p>
                <strong>Status:</strong>{' '}
                {selectedOrder.status.charAt(0).toUpperCase() + selectedOrder.status.slice(1)}
              </p>
            </div>
            <footer className="mo-modal-footer">
              {canCancelOrder(selectedOrder) && (
                <button
                    className="mo-button mo-button-danger"
                    onClick={() => handleOrderCancel(selectedOrder._id)}
                    disabled={isLoading}
                    aria-label={`Cancel order ${selectedOrder._id.slice(-6)}`}
                >
                  Cancel Order
                </button>
              )}
              <button
                className="mo-button mo-button-secondary"
                onClick={closeModal}
                aria-label="Close modal"
              >
                Close
              </button>
            </footer>
          </div>
        </div>
      )}

      {/* Cancellation Confirmation Modal */}
      {showCancelModal && (
        <div
          className="mo-modal-overlay"
          role="dialog"
          aria-labelledby="cancel-modal-title"
          onClick={closeCancelModal}
        >
          <div
            className="mo-modal"
            onClick={(e) => e.stopPropagation()}
            role="document"
            tabIndex={-1}
          >
            <header className="mo-modal-header">
              <h2 id="cancel-modal-title">Confirm Cancellation</h2>
              <button
                className="mo-modal-close"
                onClick={closeCancelModal}
                aria-label="Close cancellation modal"
              >
                ×
              </button>
            </header>
            <div className="mo-modal-body">
              <p>Are you sure you want to cancel this order?</p>
              {orderToCancel && (
                <p>
                  <strong>Order ID:</strong> {orderToCancel.slice(-6)}
                </p>
              )}
            </div>
            <footer className="mo-modal-footer">
              <button
                className="mo-button mo-button-danger"
                onClick={confirmCancel}
                disabled={isLoading}
                aria-label="Confirm order cancellation"
              >
                Confirm
              </button>
              <button
                className="mo-button mo-button-secondary"
                onClick={closeCancelModal}
                aria-label="Cancel cancellation"
              >
                Cancel
              </button>
            </footer>
          </div>
        </div>
      )}

      {/* Loader */}
      {isLoading && (
        <div className="mo-loader-overlay" aria-live="polite" aria-busy="true">
          <div className="profile-loading">Loading...</div>
        </div>
      )}
    </div>
  );
};

export default MyOrders;